import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, set } from 'firebase/database';
import { db } from '../lib/firebase.js';
import { useCurrentContext } from '../lib/useCurrentContext.jsx';
import { GOALS, DOMAINS } from '../data/gamesCatalog.js';
import './Goals.css';

const MIN_GOALS = 2;
const MAX_GOALS = 3;

// The set of goal keys the current catalog recognizes (A-E). Built
// once at module load. Used to discard stale stored values left over
// from before the goals-model migration (v2026.06.04.01), which
// otherwise count toward the max and lock the picker.
const VALID_GOAL_KEYS = new Set(GOALS.map((g) => g.key));

// Split a stored goals array into recognized keys and dropped ones.
// `account.goals` may contain old-model values (domain strings like
// 'encoding', or retired keys) that no longer match any current goal.
// Only the recognized keys should seed selection or count toward the
// max; the rest are surfaced so we can tell the resident their prior
// picks were reset.
function sanitizeStoredGoals(rawGoals) {
  const valid = [];
  let droppedCount = 0;
  if (Array.isArray(rawGoals)) {
    for (const g of rawGoals) {
      if (VALID_GOAL_KEYS.has(g)) {
        valid.push(g);
      } else {
        droppedCount += 1;
      }
    }
  }
  return { valid, droppedCount };
}

// Goals selection — the second onboarding step.
//
// The user (community resident OR individual subscriber) picks 2-3
// goals in everyday language. Each goal maps to a cognitive domain
// (verbal / memory / speed / attention / executive). The selection
// drives the recommendation engine.
//
// Identity-source-agnostic (Phase 2C):
//   Reads from useCurrentContext. Writes go to ${rtdbPath}/goals
//   regardless of whether the user lives under /accounts/ or /users/.
//
// Pre-populated from the user's existing goals if they have any,
// so this page also works as "edit goals" later.
export default function Goals() {
  const navigate = useNavigate();
  const { record: account, rtdbPath, isPreview } = useCurrentContext();

  // Selected goals as a Set for cheap membership checks. Seeded from
  // the stored goals, but only the keys the current catalog still
  // recognizes — stale values are dropped so they don't lock the
  // picker at the max.
  const [selected, setSelected] = useState(
    () => new Set(sanitizeStoredGoals(account?.goals).valid)
  );
  // True once we've detected and dropped stale stored goal keys, so
  // we can show a one-time "please re-choose" note.
  const [wasReset, setWasReset] = useState(
    () => sanitizeStoredGoals(account?.goals).droppedCount > 0
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // PREVIEW — remove when no longer needed.
  const [previewDone, setPreviewDone] = useState(false);

  // If the user lands here without having completed the assessment,
  // route them back. The assessment + goals flow is meant to be linear
  // on first run. (PREVIEW: the mock account always carries an
  // assessment stub, so this never fires in preview; guarded anyway.)
  useEffect(() => {
    if (isPreview) return;
    if (account && !account.assessment?.current) {
      navigate('/onboarding/assessment', { replace: true });
    }
  }, [account, navigate, isPreview]);

  // Re-sync local selection when account loads (for "edit goals" flow).
  // Sanitize here too — this effect fires when account.goals arrives
  // async, and must not re-introduce the stale keys the initializer
  // already dropped.
  useEffect(() => {
    if (account?.goals) {
      const { valid, droppedCount } = sanitizeStoredGoals(account.goals);
      setSelected(new Set(valid));
      if (droppedCount > 0) setWasReset(true);
    }
  }, [account?.goals]);

  if (!account) {
    return (
      <div className="container goals-page">
        <p className="goals-meta">Loading…</p>
      </div>
    );
  }

  // PREVIEW — remove when no longer needed.
  // Closing screen for the unauthenticated walkthrough. No data saved.
  if (previewDone) {
    return (
      <div className="container goals-page">
        <span className="eyebrow fade-in-1">Preview Complete</span>
        <h1 className="goals-title fade-in-2">
          That's the <em>full flow</em>.
        </h1>
        <p className="goals-lede fade-in-3">
          You just walked through the assessment, results, and goal
          selection exactly as a resident would. Nothing was saved — this
          is a preview. The selected goals were:{' '}
          {Array.from(selected).join(', ') || '(none)'}.
        </p>
        <div className="goals-actions">
          <button
            type="button"
            className="btn goals-submit"
            onClick={() => navigate('/assessment/preview', { replace: true })}
          >
            ↻ Run the preview again
          </button>
        </div>
      </div>
    );
  }

  function toggleGoal(key) {
    // Once the resident starts choosing, the reset note has served
    // its purpose — clear it.
    if (wasReset) setWasReset(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        if (next.size >= MAX_GOALS) {
          // At max — silently ignore. UI hint is shown below.
          return prev;
        }
        next.add(key);
      }
      return next;
    });
  }

  const canSubmit =
    selected.size >= MIN_GOALS && selected.size <= MAX_GOALS && !saving;

  async function handleSubmit() {
    if (!canSubmit) return;

    // PREVIEW — remove when no longer needed.
    // No Firebase write; just show the completion state to close out
    // the walkthrough.
    if (isPreview) {
      setPreviewDone(true);
      return;
    }

    if (!rtdbPath) {
      setError('Could not determine where to save your goals. Please sign in again.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const goalsArray = Array.from(selected);
      await set(
        ref(db, `${rtdbPath}/goals`),
        goalsArray
      );
      await set(
        ref(db, `${rtdbPath}/last_active`),
        Date.now()
      );
      navigate('/resident', { replace: true });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  const remaining = MAX_GOALS - selected.size;
  const hint =
    selected.size === 0
      ? `Choose ${MIN_GOALS} or ${MAX_GOALS} goals.`
      : selected.size < MIN_GOALS
      ? `Choose at least ${MIN_GOALS} goals.`
      : selected.size === MAX_GOALS
      ? "You've reached the maximum."
      : `You can choose ${remaining} more if you'd like.`;

  return (
    <div className="container goals-page">
      <span className="eyebrow fade-in-1">Onboarding · Step 2 of 2</span>
      <h1 className="goals-title fade-in-2">
        What would you like to <em>work on</em>?
      </h1>
      <p className="goals-lede fade-in-3">
        Pick two or three goals. We'll build your daily set of brain
        games around what matters most to you. You can always change
        your goals later.
      </p>

      {wasReset ? (
        <div className="goals-reset-note fade-in-3" role="status">
          We've updated our brain-health goals since you last chose
          yours. Please pick your goals again below.
        </div>
      ) : null}

      <div className="goals-hint fade-in-3" role="status">{hint}</div>

      <ul className="goals-list fade-in-4">
        {GOALS.map((goal) => {
          const domain = DOMAINS[goal.domain];
          const isSelected = selected.has(goal.key);
          const isDisabled = !isSelected && selected.size >= MAX_GOALS;
          return (
            <li key={goal.key} className="goals-item">
              <button
                type="button"
                className={
                  'goals-card' +
                  (isSelected ? ' goals-card-selected' : '') +
                  (isDisabled ? ' goals-card-disabled' : '')
                }
                onClick={() => toggleGoal(goal.key)}
                disabled={isDisabled}
                aria-pressed={isSelected}
              >
                <div
                  className="goals-card-mark"
                  style={{ background: isSelected ? domain.color : 'transparent' }}
                  aria-hidden="true"
                >
                  <span className="goals-card-icon">{domain.icon}</span>
                </div>
                <div className="goals-card-body">
                  <span className="goals-card-label">{goal.label}</span>
                  <span className="goals-card-example">{goal.example}</span>
                </div>
                <div
                  className={
                    'goals-card-check' +
                    (isSelected ? ' goals-card-check-on' : '')
                  }
                  aria-hidden="true"
                >
                  {isSelected ? '✓' : ''}
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {error ? (
        <div className="goals-error" role="alert">{error}</div>
      ) : null}

      <div className="goals-actions">
        <button
          type="button"
          className="btn goals-submit"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {saving ? 'Saving…' : 'Save my goals →'}
        </button>
      </div>
    </div>
  );
}
